import { useEffect, useMemo, useState } from "react";
import { FiClock, FiEdit3, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { BookCover } from "@/components/dashboard/BookCover";
import { Button, Dialog, Input } from "@/components/ui";
import { translateMessage } from "@/i18n";
import { useBooksQuery } from "@/queries/hooks";
import type { Book } from "@/types";
import { readTimer, READING_TIMER_CHANGED_EVENT } from "@/utils/readingTimer";

interface QuickReadingActionProps {
  userId: number;
}

function selectDefaultBook(books: Book[]): Book | undefined {
  return [...books].sort((left, right) => {
    if (left.reading_status === "reading" && right.reading_status !== "reading") {
      return -1;
    }
    if (right.reading_status === "reading" && left.reading_status !== "reading") {
      return 1;
    }
    return (right.last_read_at ?? "").localeCompare(left.last_read_at ?? "");
  })[0];
}

export function QuickReadingAction({ userId }: QuickReadingActionProps) {
  const navigate = useNavigate();
  const booksQuery = useBooksQuery({ page_size: 100 });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);
  const [hasActiveTimer, setHasActiveTimer] = useState(() => readTimer(userId) != null);
  const eligibleBooks = useMemo(
    () =>
      (booksQuery.data?.items ?? []).filter(
        (book) =>
          book.reading_status !== "finished" && book.reading_status !== "wishlist"
      ),
    [booksQuery.data]
  );
  const defaultBook = selectDefaultBook(eligibleBooks);
  const selectedBook =
    eligibleBooks.find((book) => book.id === selectedBookId) ?? defaultBook;
  const matchingBooks = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return eligibleBooks;
    return eligibleBooks.filter((book) =>
      `${book.title} ${book.author}`.toLocaleLowerCase().includes(normalized)
    );
  }, [eligibleBooks, query]);

  useEffect(() => {
    const sync = (): void => setHasActiveTimer(readTimer(userId) != null);
    window.addEventListener(READING_TIMER_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(READING_TIMER_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [userId]);

  if (hasActiveTimer) return null;

  const dispatch = (action: "log" | "timer"): void => {
    if (!selectedBook) return;
    setOpen(false);
    setQuery("");
    navigate(
      `/reading?trackAction=${action}&trackBook=${selectedBook.id}#reading-timer`
    );
  };

  return (
    <>
      <Button
        aria-label={translateMessage("reading.QuickAction.open")}
        className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] right-3 z-30 min-h-12 rounded-full px-5 shadow-soft md:bottom-8 md:right-8 md:min-h-16 md:gap-3 md:px-8 md:text-lg md:shadow-xl"
        onClick={() => setOpen(true)}
      >
        <FiClock aria-hidden="true" className="md:text-2xl" />
        {translateMessage("reading.QuickAction.read")}
      </Button>
      <Dialog
        isOpen={open}
        onClose={() => setOpen(false)}
        title={translateMessage("reading.QuickAction.title")}
      >
        {eligibleBooks.length === 0 ? (
          <p className="text-sm text-muted">
            {translateMessage("reading.QuickAction.noBooks")}
          </p>
        ) : (
          <div className="space-y-4">
            <label className="relative block">
              <span className="sr-only">
                {translateMessage("reading.QuickAction.search")}
              </span>
              <FiSearch
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-3.5 text-muted"
              />
              <Input
                aria-label={translateMessage("reading.QuickAction.search")}
                className="pl-10"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={translateMessage(
                  "reading.ReadingPage.searchByTitleOrAuthor"
                )}
                type="search"
                value={query}
              />
            </label>
            <ul className="max-h-[45dvh] space-y-2 overflow-y-auto">
              {matchingBooks.map((book) => (
                <li key={book.id}>
                  <button
                    aria-current={selectedBook?.id === book.id ? "true" : undefined}
                    className={`flex min-h-16 w-full items-center gap-3 rounded-xl border p-2 text-left ${
                      selectedBook?.id === book.id
                        ? "border-accent bg-accent/10"
                        : "border-line hover:border-accent"
                    }`}
                    onClick={() => setSelectedBookId(book.id)}
                    type="button"
                  >
                    <BookCover
                      author={book.author}
                      className="h-14 w-10 shrink-0 rounded-sm"
                      src={book.cover_image_url}
                      title={book.title}
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{book.title}</span>
                      <span className="block truncate text-sm text-muted">
                        {book.author}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="sticky bottom-0 grid grid-cols-2 gap-2 border-t border-line bg-surface pt-3">
              <Button onClick={() => dispatch("log")} variant="secondary">
                <FiEdit3 aria-hidden="true" />
                {translateMessage("reading.ReadingPage.logProgress")}
              </Button>
              <Button onClick={() => dispatch("timer")}>
                <FiClock aria-hidden="true" />
                {translateMessage("reading.ReadingPage.startReading")}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
